import { useContext, useEffect, useState } from 'react'

import { GenderOptionControl, theme } from '@hhgtech/hhg-components'
import { Button, Input, Select, Text } from '@hhgtech/hhg-components/mantine'
import { LOCALE } from '@hhgtech/hhg-components/types'
import { Box, BoxProps } from '@mantine/core'
import { useForm, yupResolver } from '@mantine/form'
import dayjs from 'dayjs'
import { useIntl } from 'react-intl'

import { AgeInput } from 'components/atoms/healthTools/form/ageInput'
import { CheckYourSelfSwitching } from 'components/atoms/healthTools/form/checkYourSelfSwitching'
import { StyledFormControl } from 'components/organisms/babyGrowthForm/index.styled'
import { useCheckingYourself } from 'hooks/healthTools/useCheckingYourself'
import { useCurrentUrl } from 'hooks/useCurrentUrl'
import { useIsMobile } from 'hooks/useIsMobile'
import { useRouterExtend } from 'hooks/useRouterExtend'
import { API_DATE_FORMAT } from 'interfaces/constants'
import {
  ACTIVITY_LEVEL_NAME,
  getActivityLevelList,
} from 'interfaces/constants/healthTools'
import { AppContext } from 'state/appContext'
import {
  formartSSOGenderToHealthtoolForm,
  checkAndSaveUserInfo,
} from 'utils/healthTools'

import * as BmrFormHelpers from './index.helpers'
import useStyles from './index.styles'
import { BmiFormHelpers } from '../bmiForm'
import { GenderOptionSelectControl } from '../genderOptionSelectControl'

const BmrForm = ({
  onSubmit: onSubmitProps,
  // template = 'embed',
  className,
  classNames,
  styles,
  ...boxProps
}: BmrFormHelpers.BmrFormProps & BoxProps) => {
  const { classes, cx } = useStyles(undefined, {
    name: 'BmrForm',
  })
  const {
    state: { userInfo, isLoggedIn },
  } = useContext(AppContext)
  const { locale, router } = useRouterExtend()
  const { formatMessage: f } = useIntl()
  const [loading, setLoading] = useState(false)
  const {
    isCheckYourself,
    setIsCheckYourself,
    checkAvailableGenderOpt,
  } = useCheckingYourself()
  const form = useForm<BmrFormHelpers.BmrFormValue>({
    validate: yupResolver(BmrFormHelpers.generateSchema(f, locale)),
    initialValues: {
      height: '',
      weight: '',
      age: '',
      gender: '',
    },
  })

  const currentUrl = useCurrentUrl()
  const bmiGenerated = BmrFormHelpers.generateBmrRules({ f, locale })
  const bmiUnit = BmrFormHelpers.bmrUnitValidation[locale]
  const isMobile = useIsMobile()

  const onValid = async (data: BmrFormHelpers.BmrFormValue) => {
    const height = bmiUnit.heightConvert(Number(data.height))
    const weight = bmiUnit.weightConvert(Number(data.weight))
    const {
      age = '',
      activityLevel = ACTIVITY_LEVEL_NAME.LOW_INTENSITY,
      gender,
    } = data
    const submitValue: BmrFormHelpers.BmrFormValue = {
      gender: data.gender,
      age: data.age,
      height: height.toString(),
      weight: weight.toString(),
      activityLevel: data.activityLevel,
      bmr: await BmrFormHelpers.getBmr({
        height,
        weight,
        age,
        gender,
        activityLevel,
      }),
      isCheckYourself,
    }
    if (userInfo?.id && isCheckYourself) {
      setLoading(true)
      await checkAndSaveUserInfo({ userInfo, data })
      setLoading(false)
    }

    if (!userInfo?.id) {
      BmiFormHelpers.syncParamsToUseOnSSOForm(
        {
          gender: data.gender,
          birthday: data.birthday,
        },
        router,
      )
    }

    onSubmitProps?.(submitValue)
  }

  useEffect(() => {
    if (isCheckYourself) {
      userInfo?.gender !== -1 &&
        form.setFieldValue(
          'gender',
          formartSSOGenderToHealthtoolForm(userInfo?.gender || 0),
        )

      userInfo?.height && form.setFieldValue('height', userInfo?.height)
      userInfo?.weight && form.setFieldValue('weight', userInfo?.weight)
      form.setFieldValue(
        'activityLevel',
        userInfo?.activity_level || ACTIVITY_LEVEL_NAME.LOW_INTENSITY,
      )
      if (userInfo?.birthday) {
        form.setFieldValue(
          'birthday',
          dayjs(userInfo?.birthday, API_DATE_FORMAT).toDate(),
        )
        form.setFieldValue(
          'age',
          String(
            dayjs().diff(dayjs(userInfo?.birthday, API_DATE_FORMAT), 'year'),
          ),
        )
      }
    } else {
      form.reset()
    }
  }, [isCheckYourself, userInfo])

  useEffect(() => {
    isLoggedIn && setIsCheckYourself(true)
  }, [isLoggedIn])

  return (
    <Box className={cx(className, classes.root)} {...boxProps}>
      <form onSubmit={form.onSubmit(onValid)}>
        <Box className={cx('age-group', classes.group)}>
          {isLoggedIn && (
            <Box className={cx('age-item', classes.item)}>
              <StyledFormControl>
                <CheckYourSelfSwitching
                  checked={isCheckYourself}
                  onChange={(e) => setIsCheckYourself(e.currentTarget.checked)}
                />
              </StyledFormControl>
            </Box>
          )}
          <StyledFormControl className={cx('age-item', classes.item)}>
            <AgeInput
              {...form.getInputProps('birthday')}
              disableSSOAge={!isCheckYourself}
              onChange={(v) => {
                form.setFieldValue('age', String(dayjs().diff(v, 'year')))
                form.setFieldValue('birthday', v as Date)
              }}
            />
          </StyledFormControl>
        </Box>
        <Box className={classes.group}>
          <Box className={cx('gender', classes.item)}>
            {isMobile ? (
              <GenderOptionControl
                label={bmiGenerated.gender?.label}
                availableValue={checkAvailableGenderOpt(
                  form.getInputProps('gender').value,
                )}
                {...form.getInputProps('gender')}
                styles={{
                  wrapper: {
                    gap: 12,
                  },
                }}
              />
            ) : (
              <GenderOptionSelectControl
                label={bmiGenerated.gender?.label}
                availableValue={checkAvailableGenderOpt(
                  form.getInputProps('gender').value,
                )}
                {...form.getInputProps('gender')}
              />
            )}
          </Box>
          <Box className={classes.item}>
            <Input.Number
              label={
                <Text size="s4" weight="semiBold" color={theme.colors.gray600}>
                  {bmiGenerated.height?.label}
                </Text>
              }
              placeholder={f(
                { id: 'tools.inputPlaceholder' },
                { content: locale === LOCALE.Myanmar ? 6 : 170 },
              )}
              name="height"
              {...{
                ...form.getInputProps('height'),
                value: form.getInputProps('height').value
                  ? Number(form.getInputProps('height').value)
                  : '',
              }}
              min={0}
              precision={1}
              inputMode="decimal"
              classNames={{
                rightSection: classes.inputRightSection,
              }}
              rightSection={f({
                id: 'bmi.heightInput.desc',
                defaultMessage: '(cm)',
              })}
              rightSectionWidth={60}
            />
          </Box>
          <Box className={classes.item}>
            <Input.Number
              label={
                <Text size="s4" weight="semiBold" color={theme.colors.gray600}>
                  {bmiGenerated.weight?.label}
                </Text>
              }
              name="weight"
              {...{
                ...form.getInputProps('weight'),
                value: form.getInputProps('weight').value
                  ? Number(form.getInputProps('weight').value)
                  : '',
              }}
              placeholder={f(
                { id: 'tools.inputPlaceholder' },
                { content: locale === LOCALE.Myanmar ? 130 : 60 },
              )}
              precision={1}
              inputMode="decimal"
              min={0}
              rightSection={f({
                id: 'bmi.weightInput.desc',
                defaultMessage: '(kg)',
              })}
              rightSectionWidth={60}
              classNames={{
                rightSection: classes.inputRightSection,
              }}
            />
          </Box>
          <Box className={classes.longItem}>
            <Select
              label={
                <Text size="s4" weight="semiBold" color={theme.colors.gray600}>
                  {bmiGenerated.activityLevel?.label}
                </Text>
              }
              data={getActivityLevelList(f)}
              placeholder={f({ id: 'common.placeholder' })}
              {...form.getInputProps('activityLevel')}
              styles={{
                item: {
                  fontSize: 14,
                  wordBreak: 'break-word',
                },
              }}
            />
          </Box>
        </Box>
        <Button
          loading={loading}
          size="md"
          type="submit"
          className={cx('bmi-btn bmi--btn-submit', classes.btn)}
          data-event-category="Health Tool"
          data-event-action="BMR"
          data-event-label={currentUrl}
        >
          {f({
            id: 'bmi.submit',
            defaultMessage: 'Calculate',
          })}
        </Button>
      </form>
    </Box>
  )
}

export { BmrForm, BmrFormHelpers }
